<template>
  <v-card class="admin-side-menu" flat tile>
    <!-- Header: icon-only home mark + wordmark -->
    <div class="admin-side-menu__header">
      <v-img
        class="admin-side-menu__mark"
        max-height="24px"
        max-width="24px"
        :src="require('@components/assets/clientlogo.svg')"
        alt="exIQtive"
      />
      <span class="admin-side-menu__wordmark">exIQtive.com</span>
    </div>

    <!-- Primary navigation (flat links, no expand/collapse) -->
    <div class="admin-side-menu__body">
      <v-list dense nav class="admin-side-menu__list">
        <v-list-item
          v-for="item in menuItem1"
          :key="item.title"
          link
          :to="item.to"
          :data-test="`admin-nav-${item.title}`"
          class="admin-nav-item"
          active-class="admin-nav-item--active"
        >
          <v-list-item-avatar class="admin-nav-item__avatar" rounded>
            <v-img max-height="24px" max-width="24px" :src="item.icon" :alt="item.title" />
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="admin-nav-item__title">
              {{ item.title }}
            </v-list-item-title>
          </v-list-item-content>
          <span v-if="item.count" class="admin-nav-item__badge">
            {{ item.count }}
          </span>
        </v-list-item>
      </v-list>
    </div>

    <!-- Footer: secondary links + account row -->
    <div class="admin-side-menu__footer">
      <v-list dense class="admin-side-menu__list">
        <v-list-item
          v-for="item in menuItem2"
          :key="item.title"
          link
          :to="item.to"
          :data-test="`admin-footer-${item.title}`"
          class="admin-nav-item"
          active-class="admin-nav-item--active"
        >
          <v-list-item-avatar class="admin-nav-item__avatar" rounded>
            <v-img max-height="24px" max-width="24px" :src="item.icon" :alt="item.title" />
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="admin-nav-item__title">
              {{ item.title }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-divider class="admin-side-menu__divider" />

        <v-list-item class="admin-user-row">
          <v-list-item-avatar color="grey darken-3" size="40" class="admin-user-row__avatar">
            <ExAvatar
              size="medium"
              :picture="userAvatar"
              :firstName="userData ? userData.first_name : ''"
              :lastName="userData ? userData.last_name : ''"
            />
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="admin-user-row__name">
              {{ userData ? `${userData.first_name} ${userData.last_name}` : "Unknown User" }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="userData && userData.email" class="admin-user-row__email">
              {{ userData.email }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-btn
            icon
            class="admin-user-row__signout"
            aria-label="Sign out"
            data-test="admin-sign-out"
            @click="logout"
          >
            <v-icon>mdi-logout</v-icon>
          </v-btn>
        </v-list-item>
      </v-list>
    </div>
  </v-card>
</template>

<script setup>
import { computed, getCurrentInstance } from "vue";
import ExAvatar from "@components/common/ExAvatar.vue";

const props = defineProps({
  menuItem1: {
    type: Array,
    default: () => [],
  },
  menuItem2: {
    type: Array,
    default: () => [],
  },
  // Optional user override — the owning screen normally leaves this null and
  // the component reads the current admin user from the store/localStorage.
  user: {
    type: Object,
    default: null,
  },
});

const instance = getCurrentInstance();
const { proxy } = instance || {};

const userData = computed(() => {
  if (props.user) {
    return props.user;
  }
  try {
    if (proxy && proxy.$store && proxy.$store.getters && proxy.$store.getters.userData) {
      return proxy.$store.getters.userData;
    }
  } catch (e) {
    /* ignore store access errors (e.g. store not provided) */
  }
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
});

const userAvatar = computed(() => {
  return (userData.value && (userData.value.picture || userData.value.avatar)) || null;
});

function logout() {
  try {
    if (proxy && proxy.$store && proxy.$store.dispatch) {
      proxy.$store.dispatch("logout");
    }
  } catch (e) {
    /* ignore store dispatch errors */
  }
  // Clear Sentry user and secure config before wiping localStorage.
  try {
    const Sentry = require("@sentry/vue").default;
    Sentry.setUser(null);
  } catch (e) {
    /* Sentry may not be installed in this bundle */
  }
  try {
    const configService = require("@/services/config").default;
    configService.clearSecureConfig();
  } catch (e) {
    /* config service may not be available */
  }
  try {
    localStorage.clear();
  } catch (e) {
    /* ignore storage access errors */
  }
  if (proxy && proxy.$router) {
    proxy.$router.push({ path: "/login" }).catch((err) => {
      console.error("Failed to navigate:", err);
    });
  }
}
</script>

<style scoped>
.admin-side-menu {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #ffffff;
}

.admin-side-menu__header {
  display: flex;
  align-items: center;
  padding: 24px 20px 16px;
}

.admin-side-menu__mark {
  width: 24px;
  height: 24px;
  margin-right: 12px;
}

.admin-side-menu__wordmark {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #182230;
}

.admin-side-menu__body {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px;
}

.admin-side-menu__footer {
  padding: 8px 12px 16px;
  border-top: 1px solid #eaecf0;
}

.admin-side-menu__list {
  padding: 0;
}

.admin-side-menu__divider {
  margin: 8px 0;
}

.admin-side-menu ::v-deep .admin-nav-item {
  border-radius: 8px;
  margin-bottom: 2px;
}

.admin-side-menu ::v-deep .admin-nav-item::before {
  border-radius: 8px;
  background-color: transparent;
}

.admin-side-menu ::v-deep .admin-nav-item:hover::before {
  background-color: #f2f4f7;
  opacity: 1;
}

.admin-side-menu ::v-deep .admin-nav-item--active,
.admin-side-menu ::v-deep .admin-nav-item--active:hover {
  background-color: #e4e4e4;
}

.admin-side-menu ::v-deep .admin-nav-item--active::before,
.admin-side-menu ::v-deep .admin-nav-item--active:hover::before {
  background-color: transparent;
}

.admin-side-menu ::v-deep .admin-nav-item__avatar {
  min-width: 24px;
  margin-right: 12px;
}

.admin-side-menu ::v-deep .admin-nav-item__title {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #344054;
}

.admin-side-menu ::v-deep .admin-nav-item--active .admin-nav-item__title {
  color: #182230;
}

.admin-nav-item__badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 25px;
  height: 22px;
  padding: 0 8px;
  border: 1px solid #eaecf0;
  border-radius: 100px;
  background-color: #f9fafb;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
}

.admin-side-menu ::v-deep .admin-user-row {
  margin-top: 4px;
}

.admin-side-menu ::v-deep .admin-user-row__avatar {
  margin-right: 12px;
}

.admin-side-menu ::v-deep .admin-user-row__name {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.admin-side-menu ::v-deep .admin-user-row__email {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.admin-side-menu ::v-deep .admin-user-row__signout {
  color: #344054;
}
</style>
